// Транспорт раздела «Проекты координатора»: карточки, вехи, чек-листы, журнал.
// ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ
// Источник — брифинг, а не портал 73 и не Bitrix: у него свой синк, свой префикс зеркала
// и свои оговорки. Складывать его хуки в чужой файл значило бы однажды переиспользовать
// «покажи проекты» внутри раздела с другими правами.
//
// Записи здесь нет и появиться не должно: master — брифинг, координаторы работают в нём.
// Раздел в CRM — обзор.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/pbr'

/** Сколько строк на странице. То же число, что у соседних разделов зеркала. */
export const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Тот же разбор ответа, что в api/client.ts и api/carsharing.ts. Продублирован
 * намеренно: общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк
 * значило бы поставить всем разделам общую точку отказа.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Проекты координатора» вам не открыт: он показывает свод '
        + 'по всем координаторам сразу и зоной ответственности не сужается')
    }
    // 400 от ядра здесь — это неверный фильтр. Пустая таблица читалась бы как
    // «проектов нет», а это ложь про работу людей.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. pbr_api.py) ───────────────

export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
}

/**
 * Карточка проекта в списке.
 *
 * `filled` — есть ли в карточке хоть что-то, кроме двух автоматических записей.
 * Это не украшение: карточка заводится сама при первом открытии экрана проекта на
 * брифинге, и без этого признака список читался бы как «проектов ведётся столько-то».
 *
 * Судна, компании и заголовка сделки в источнике нет — они приходят из зеркала Bitrix.
 * Поэтому `deal_in_mirror` может быть false, и тогда пусто именно у них, а не у карточки.
 */
export interface ПроектСтрока {
  deal_bitrix_id: number
  deal_title: string | null
  deal_in_mirror: boolean
  deal_stage_id: string | null
  deal_closed: boolean | null
  company_id: number | null
  company_title: string | null
  vessel_contact_id: number | null
  vessel_name: string | null
  stage: string | null
  closed: boolean | null
  coordinator_bx_id: number | null
  coordinator_name: string | null
  manager_bx_id: number | null
  created_at: string | null
  created_by: string | null
  updated_at: string | null
  milestones_total: number | null
  milestones_done: number | null
  checklist_total: number | null
  checklist_done: number | null
  events_total: number | null
  events_own: number | null
  last_event_at: string | null
  last_event_type: string | null
  filled: boolean | null
  has_notes: boolean | null
}

export interface ПроектыИтоги {
  projects: number | null
  filled: number | null
  coordinators: number | null
  milestones: number | null
  checklist_done: number | null
  events_own: number | null
  no_deal: number | null
}

export interface ВехаСтрока {
  ms_id: string
  title: string | null
  start_date: string | null
  end_date: string | null
  status: string | null
  progress: number | null
  created_at: string | null
}

export interface ПунктСтрока {
  item_key: string
  done: boolean | null
  updated_at: string | null
  updated_by: string | null
  has_comment: boolean | null
}

/**
 * Запись журнала.
 *
 * `text_hidden` — текст записи намеренно не перенесён: в нём суммы, часы, километры
 * или фамилии бригады. Сама запись при этом видна, и экран обязан сказать почему,
 * а не показать пустую ячейку.
 */
export interface СобытиеСтрока {
  deal_bitrix_id: number
  ev_id: string
  ts: string | null
  ev_type: string | null
  by_login: string | null
  ev_text: string | null
  text_hidden: boolean | null
  deal_title?: string | null
  coordinator_name?: string | null
}

export interface СобытияИтоги {
  events: number | null
  hidden: number | null
  auto: number | null
  deals: number | null
}

export interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

export interface КоординаторСтрока {
  coordinator_bx_id: number | null
  coordinator_name: string | null
  n: number | null
  filled: number | null
}

export interface СправочникиПроектов {
  source: string
  counts: Record<string, number | string | null>
  freshness: Свежесть[]
  stages: { stage: string | null; n: number }[]
  coordinators: КоординаторСтрока[]
  event_types: { ev_type: string | null; n: number }[]
  stage_names: Record<string, string>
  item_names: Record<string, string>
  milestone_status_names: Record<string, string>
  event_type_names: Record<string, string>
  auto_types: string[]
  sort_list: string[]
  data_notes: Record<string, string>
  caveats: string[]
}

export interface Карточка {
  project: ПроектСтрока
  milestones: ВехаСтрока[]
  checklist: ПунктСтрока[]
  events: СобытиеСтрока[]
  events_total: number
  next: number | null
  limit: number
  start: number
}

// ─────────────────────────────── хуки ───────────────────────────────

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useСправочникиПроектов() {
  return useQuery<СправочникиПроектов>({
    queryKey: ['coord-projects', 'refs'],
    queryFn: () => запрос<СправочникиПроектов>('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useПроекты(ф: Record<string, unknown>) {
  return useQuery<Список<ПроектСтрока> & { totals: ПроектыИтоги }>({
    queryKey: ['coord-projects', 'projects', ф],
    queryFn: () => запрос('/projects' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useЖурналПроектов(ф: Record<string, unknown>) {
  return useQuery<Список<СобытиеСтрока> & { totals: СобытияИтоги }>({
    queryKey: ['coord-projects', 'events', ф],
    queryFn: () => запрос('/events' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/** Одна карточка целиком. `deal` пустой — не ходим вовсе, а не спрашиваем «ничего». */
export function useКарточкаПроекта(deal?: number) {
  return useQuery<Карточка>({
    queryKey: ['coord-projects', 'project', deal],
    queryFn: () => запрос(`/project?deal=${deal}`),
    enabled: !!deal,
    ...ОБЩЕЕ,
  })
}

/**
 * Оговорки раздела, которые экран обязан произнести, и порядок их чтения.
 * Тексты и числа в них считает ядро — здесь только ключи: свой текст неизбежно
 * оброс бы вбитым числом, а оно однажды разойдётся с тем, что человек видит рядом.
 */
export const ОГОВОРКИ_ПРОЕКТОВ = [
  'auto_created',
  'no_write',
  'no_money',
  'no_journal',
  'no_tasks',
  'events_hidden',
  'events_auto',
  'events_trimmed',
  'checklist_template',
  'notes_hidden',
  'deal_link',
  'gone',
]

/** Как называется сущность зеркала в подписи «обновлено N мин назад». */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'pbr:projects': 'карточки',
  'pbr:milestones': 'вехи',
  'pbr:checklist': 'чек-листы',
  'pbr:events': 'журнал',
}

/** Самая отставшая из сущностей раздела. Свежесть показываем по ней, а не по средней. */
export function самаяСтарая(список?: Свежесть[]): Свежесть | undefined {
  const свои = (список ?? []).filter((с) => с.entity in ИМЯ_СУЩНОСТИ)
  if (!свои.length) return undefined
  return свои.reduce((а, б) => ((б.age_min ?? 0) > (а.age_min ?? 0) ? б : а))
}
